<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { motion } from 'motion-v'
import type { AiScreeningData } from '@/components/recruitment/AiScreeningPanel.vue'

const { t } = useI18n()

const props = defineProps<{
  aiScreening: AiScreeningData
}>()

type Line = {
  key: string
  label?: string
  value?: string
  style?: string
  isSeparator?: boolean
  summary?: string
}

const isLoading = computed(() => {
  return ['pending', 'queued', 'processing'].includes(props.aiScreening?.status || '')
})

const hasResult = computed(() => props.aiScreening?.status === 'completed')

const scoreStyle = (score: number | null | undefined) => {
  if (score == null) return 'text-subtitle'
  if (score >= 8) return 'text-success-500 font-semibold'
  if (score >= 5) return 'text-warning-500 font-semibold'
  return 'text-danger-500 font-semibold'
}

const statusLabel = computed(() => {
  const status = props.aiScreening?.status
  if (!status) return ''
  return t(`modules.recruitment.aiScreening.status.${status}`)
})

const lines = computed<Line[]>(() => {
  const items: Line[] = []
  const screening = props.aiScreening

  items.push({
    key: 'title',
    label: t('components.recruitment.aiScreening.tooltip.title'),
    style: 'font-semibold subtitle text-[10px] uppercase tracking-wide',
  })

  if (isLoading.value) {
    items.push({ key: 'sep-loading', isSeparator: true })
    items.push({
      key: 'status',
      label: statusLabel.value,
      value: '…',
      style: 'text-warning-500',
    })
    items.push({
      key: 'hint',
      summary: t('modules.recruitment.aiScreening.pendingHint'),
    })
    return items
  }

  if (screening.status === 'failed' || screening.status === 'skipped') {
    items.push({ key: 'sep-error', isSeparator: true })
    items.push({
      key: 'status',
      label: statusLabel.value,
      style: 'text-subtitle font-medium',
    })
    if (screening.error) {
      items.push({ key: 'error', summary: screening.error })
    }
    return items
  }

  if (!hasResult.value) return items

  items.push({ key: 'sep-score', isSeparator: true })
  items.push({
    key: 'overall',
    label: t('components.recruitment.aiScreening.tooltip.overall'),
    value: `${screening.overallScore}/10`,
    style: scoreStyle(screening.overallScore),
  })

  if (screening.overallSummary) {
    items.push({ key: 'overall-summary', summary: screening.overallSummary })
  }

  if (screening.resumeQuality?.score != null) {
    items.push({ key: 'sep-cv', isSeparator: true })
    items.push({
      key: 'cv',
      label: t('components.recruitment.aiScreening.tooltip.cv'),
      value: `${screening.resumeQuality.score}/10`,
      style: scoreStyle(screening.resumeQuality.score),
    })
    if (screening.resumeQuality.summary) {
      items.push({ key: 'cv-summary', summary: screening.resumeQuality.summary })
    }
  }

  if (screening.coverLetterQuality?.score != null) {
    items.push({ key: 'sep-cl', isSeparator: true })
    items.push({
      key: 'cl',
      label: t('components.recruitment.aiScreening.tooltip.coverLetter'),
      value: `${screening.coverLetterQuality.score}/10`,
      style: scoreStyle(screening.coverLetterQuality.score),
    })
    if (screening.coverLetterQuality.summary) {
      items.push({ key: 'cl-summary', summary: screening.coverLetterQuality.summary })
    }
  }

  if (screening.jobFit?.score != null) {
    items.push({ key: 'sep-fit', isSeparator: true })
    items.push({
      key: 'fit',
      label: t('components.recruitment.aiScreening.tooltip.jobFit'),
      value: `${screening.jobFit.score}/10`,
      style: scoreStyle(screening.jobFit.score),
    })
    if (screening.jobFit.summary) {
      items.push({ key: 'fit-summary', summary: screening.jobFit.summary })
    }
    if (screening.jobFit.matchPoints?.length) {
      items.push({
        key: 'fit-match',
        summary: screening.jobFit.matchPoints.map(point => `• ${point}`).join('\n'),
      })
    }
    if (screening.jobFit.gaps?.length) {
      items.push({
        key: 'fit-gaps',
        summary: screening.jobFit.gaps.map(gap => `• ${gap}`).join('\n'),
        style: 'text-subtitle',
      })
    }
  }

  return items
})

const getDelay = (index: number) => index * 0.025
const itemInitial = { opacity: 0, y: 4 }
const itemAnimate = { opacity: 1, y: 0 }
const itemTransition = (index: number) => ({ duration: 0.15, ease: 'easeOut' as const, delay: getDelay(index) })
</script>

<template>
  <div class="flex flex-col gap-0.5 p-1.5 w-92 max-w-92" data-cy="ai-screening-tooltip">
    <template v-for="(line, index) in lines" :key="line.key">
      <motion.div
        v-if="line.isSeparator"
        :initial="{ opacity: 0 }"
        :animate="{ opacity: 0.3 }"
        :transition="itemTransition(index)"
        class="border-b border-current my-0.5"
      />
      <motion.div
        v-else-if="line.label && line.value"
        :initial="itemInitial"
        :animate="itemAnimate"
        :transition="itemTransition(index)"
        class="flex justify-between gap-4 text-xs"
        :class="line.style"
        :data-cy="`ai-screening-line-${line.key}`"
      >
        <span>{{ line.label }}</span>
        <span class="tabular-nums shrink-0">{{ line.value }}</span>
      </motion.div>
      <motion.div
        v-else-if="line.label && !line.value"
        :initial="itemInitial"
        :animate="itemAnimate"
        :transition="itemTransition(index)"
        class="text-xs"
        :class="line.style"
        :data-cy="`ai-screening-line-${line.key}`"
      >
        <span>{{ line.label }}</span>
      </motion.div>
      <motion.p
        v-else-if="line.summary"
        :initial="itemInitial"
        :animate="itemAnimate"
        :transition="itemTransition(index)"
        class="text-[10px] leading-snug wrap-break-word whitespace-pre-line"
        :class="line.style || 'subtitle'"
        :data-cy="`ai-screening-line-${line.key}`"
      >
        {{ line.summary }}
      </motion.p>
    </template>
  </div>
</template>
